import { useState, useRef, useEffect } from 'react';
import { Input, Spinner } from '@heroui/react';
import { X, Plus, ChevronDown } from 'lucide-react';
import { useCompanies, useCompany, useCreateCompany } from '@/api/companies';
import type { CompanyTypeEnum } from '@estimator/shared';

interface CompanyComboboxProps {
  value: string | null;
  onChange: (id: string | null) => void;
  label: string;
  isRequired?: boolean;
}

const COMPANY_TYPE_COLORS: Record<string, string> = {
  CONTRACTOR: '#86efac',
  ARCHITECT: '#a5b4fc',
  MANUFACTURER: '#fbbf24',
  DESIGNER: '#d8b4fe',
  GENERAL_CONTRACTOR: '#86efac',
  SUBCONTRACTOR: '#67e8f9',
  SUPPLIER: '#fca5a5',
  OTHER: '#94a3b8',
};

export default function CompanyCombobox({ value, onChange, label, isRequired }: CompanyComboboxProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data: suggestions, isLoading } = useCompanies(
    debouncedSearch ? { search: debouncedSearch, limit: 10 } : { limit: 10 },
  );

  const { data: selected } = useCompany(value);

  const createCompany = useCreateCompany();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setShowCreateForm(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (companyId: string) => {
    onChange(companyId);
    setSearch('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setSearch('');
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    const company = await createCompany.mutateAsync({
      name: newName.trim(),
      types: [],
      phone: newPhone || undefined,
      email: newEmail || undefined,
    });
    handleSelect(company.id);
    setShowCreateForm(false);
    setNewName('');
    setNewPhone('');
    setNewEmail('');
  };

  const inputClassNames = {
    inputWrapper: 'border border-[var(--border)] bg-[var(--bg-panel)]',
    input: 'text-[var(--text-primary)]',
    label: 'text-[var(--text-secondary)]',
  };

  if (value && selected) {
    return (
      <div>
        <label className="text-xs" style={{ color: 'var(--text-secondary)' }}>
          {label} {isRequired && '*'}
        </label>
        <div
          className="flex items-center justify-between border rounded-lg px-3 py-2 mt-1"
          style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-panel)',
            color: 'var(--text-primary)',
          }}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm">{selected.name}</span>
            {selected.types?.map((t: string) => (
              <span
                key={t}
                className="text-[10px] px-1.5 py-0.5 rounded-full"
                style={{
                  backgroundColor: `${COMPANY_TYPE_COLORS[t] ?? '#94a3b8'}22`,
                  color: COMPANY_TYPE_COLORS[t] ?? '#94a3b8',
                }}
              >
                {t}
              </span>
            ))}
          </div>
          <button onClick={handleClear} className="p-0.5 hover:opacity-70">
            <X size={14} style={{ color: 'var(--text-secondary)' }} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <Input
        label={label}
        placeholder="Search companies..."
        value={search}
        onValueChange={(v) => {
          setSearch(v);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        isRequired={isRequired}
        variant="bordered"
        classNames={inputClassNames}
        endContent={
          isLoading ? (
            <Spinner size="sm" />
          ) : (
            <ChevronDown size={14} style={{ color: 'var(--text-secondary)' }} />
          )
        }
      />

      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 border rounded-lg shadow-lg overflow-hidden"
          style={{
            backgroundColor: 'var(--bg-panel)',
            borderColor: 'var(--border)',
          }}
        >
          <div className="max-h-48 overflow-y-auto">
            {(suggestions?.data ?? []).map((company) => (
              <button
                key={company.id}
                className="w-full text-left px-3 py-2 hover:bg-[var(--bg-surface)] transition-colors"
                onClick={() => handleSelect(company.id)}
              >
                <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
                  {company.name}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {company.types?.map((t: string) => (
                    <span
                      key={t}
                      className="text-[9px] px-1.5 py-0.5 rounded-full"
                      style={{
                        backgroundColor: `${COMPANY_TYPE_COLORS[t] ?? '#94a3b8'}22`,
                        color: COMPANY_TYPE_COLORS[t] ?? '#94a3b8',
                      }}
                    >
                      {t}
                    </span>
                  ))}
                  {company.phone && (
                    <span className="text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                      {company.phone}
                    </span>
                  )}
                </div>
              </button>
            ))}

            {suggestions?.data?.length === 0 && !isLoading && (
              <div className="px-3 py-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                No companies found
              </div>
            )}
          </div>

          {!showCreateForm ? (
            <button
              className="w-full text-left px-3 py-2 border-t flex items-center gap-1.5 hover:bg-[var(--bg-surface)]"
              style={{ borderColor: 'var(--border)', color: 'var(--accent)' }}
              onClick={() => {
                setShowCreateForm(true);
                setNewName(search);
              }}
            >
              <Plus size={14} />
              <span className="text-sm">Create new company</span>
            </button>
          ) : (
            <div
              className="border-t p-3 flex flex-col gap-2"
              style={{ borderColor: 'var(--border)' }}
            >
              <Input
                size="sm"
                label="Name"
                value={newName}
                onValueChange={setNewName}
                variant="bordered"
                isRequired
                classNames={inputClassNames}
              />
              <div className="flex gap-2">
                <Input
                  size="sm"
                  label="Phone"
                  value={newPhone}
                  onValueChange={setNewPhone}
                  variant="bordered"
                  classNames={inputClassNames}
                />
                <Input
                  size="sm"
                  label="Email"
                  value={newEmail}
                  onValueChange={setNewEmail}
                  variant="bordered"
                  classNames={inputClassNames}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  className="text-xs px-2 py-1"
                  style={{ color: 'var(--text-secondary)' }}
                  onClick={() => setShowCreateForm(false)}
                >
                  Cancel
                </button>
                <button
                  className="text-xs px-3 py-1 rounded"
                  style={{ backgroundColor: 'var(--accent)', color: 'white' }}
                  onClick={handleCreate}
                  disabled={!newName.trim() || createCompany.isPending}
                >
                  {createCompany.isPending ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
